import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/design";
import { haptic, tg } from "@/lib/telegram";

export function WelcomePage() {
  const navigate = useNavigate();

  useEffect(() => {
    const startParam = tg?.initDataUnsafe?.start_param;
    if (startParam?.startsWith("chatshare_")) {
      navigate(`/app/chats?share=${encodeURIComponent(startParam.slice("chatshare_".length))}`, { replace: true });
    }
  }, [navigate]);

  return (
    <div className="relative min-h-[100dvh] flex flex-col px-6 pt-safe pb-safe overflow-hidden isolate">
      {/* Warm sun — anchored top-right, half-cut by the right edge. */}
      <div className="pointer-events-none absolute inset-0 z-0">
        {/* Outermost atmospheric haze */}
        <div
          aria-hidden
          className="absolute top-0 right-0 w-[180%] aspect-square rounded-full"
          style={{
            transform: "translate(38%, -42%)",
            background:
              "radial-gradient(closest-side, rgba(255,90,20,0.85) 0%, rgba(255,107,53,0.6) 18%, rgba(255,69,0,0.32) 38%, rgba(120,30,0,0.12) 58%, rgba(0,0,0,0) 75%)",
            filter: "blur(30px)",
          }}
        />
        {/* Hot core */}
        <div
          aria-hidden
          className="absolute top-0 right-0 w-[130%] aspect-square rounded-full"
          style={{
            transform: "translate(35%, -38%)",
            background:
              "radial-gradient(closest-side, rgba(255,150,70,1) 0%, rgba(255,100,30,0.85) 20%, rgba(255,69,0,0.5) 40%, rgba(0,0,0,0) 70%)",
            filter: "blur(34px)",
            mixBlendMode: "screen",
          }}
        />
        {/* Bright nucleus / disk */}
        <div
          aria-hidden
          className="absolute top-0 right-0 w-[80%] aspect-square rounded-full"
          style={{
            transform: "translate(32%, -32%)",
            background:
              "radial-gradient(closest-side, rgba(255,220,170,0.95) 0%, rgba(255,160,80,0.75) 22%, rgba(255,100,30,0.4) 48%, rgba(0,0,0,0) 75%)",
            filter: "blur(20px)",
            mixBlendMode: "screen",
          }}
        />
        {/* White-hot pinhole */}
        <div
          aria-hidden
          className="absolute top-0 right-0 w-[34%] aspect-square rounded-full"
          style={{
            transform: "translate(28%, -28%)",
            background:
              "radial-gradient(closest-side, rgba(255,240,210,0.9) 0%, rgba(255,180,120,0.5) 40%, rgba(0,0,0,0) 80%)",
            filter: "blur(12px)",
            mixBlendMode: "screen",
          }}
        />
        {/* Film grain to kill banding */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.05] mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
          }}
        />
        {/* Bottom fade — softer, only the last ~38% to keep CTA legible */}
        <div
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-[42%]"
          style={{
            background:
              "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.45) 50%, rgba(0,0,0,0.92) 100%)",
          }}
        />
      </div>

      <div className="relative z-10 flex-1" />

      {/* Brand mark */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 flex items-center gap-2 mb-6"
      >
        <div className="size-9 rounded-xl bg-gradient-accent grid place-items-center shadow-[0_8px_24px_-8px_rgba(255,69,0,0.6)]">
          <Sparkles className="size-4 text-white" />
        </div>
        <div className="text-lg font-semibold tracking-tight">SilentPoster</div>
      </motion.div>

      {/* Headline */}
      <motion.h1
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 text-[44px] leading-[1.05] font-semibold tracking-[-0.03em]"
      >
        Объявления, которые{" "}
        <span className="text-gradient-accent">всегда наверху</span>
      </motion.h1>

      {/* Subhead */}
      <motion.p
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 text-[15px] text-text-muted mt-4 leading-relaxed max-w-[90%]"
      >
        Авто-постинг в чаты услуг от имени ваших аккаунтов. Гаранты, тайминги и
        анти-спам — на автопилоте.
      </motion.p>

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 flex flex-col gap-3 mt-8"
      >
        <Button
          size="lg"
          fullWidth
          rightIcon={<ArrowRight />}
          onClick={() => {
            haptic.impact("medium");
            navigate("/app");
          }}
        >
          Начать
        </Button>
        <button
          onClick={() => {
            haptic.select();
          }}
          className="h-12 text-sm text-text-muted hover:text-white transition-colors"
        >
          Как это работает?{" "}
          <span className="text-accent font-medium">Узнать</span>
        </button>
      </motion.div>
    </div>
  );
}
